import {useEffect, useRef, useState, type ReactNode} from 'react'
import {Link, useLocation} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Tag} from '@/components/ui/tag'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {HelpIcon} from '@/components/help-icon'
import {EvidencePanel} from '@/components/evidence-panel'
import {ActionNotesThread} from '@/components/action-notes-thread'
import {BateriaSolicitudBlock} from '@/components/bateria-solicitud-block'
import {ConstructorBlock} from '@/components/sgsst/constructor-block'
import {AccionSugeridaList} from '@/components/accion-sugerida-list'
import {PerfilSociodemograficoCard} from '@/components/perfil-sociodemografico-card'
import {gateTooltip} from './gate-tooltip'
import {useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {type Employee} from '@/hooks/empresa/identidad/use-employees'
import {type Evidence} from '@/hooks/sgsst/evidencias/use-evidence'
import {type Solicitud} from '@/hooks/sgsst/evidencias/use-solicitudes'
import {NOTAS_PAGE_SIZE, useAddItemNote, useDeleteItemNote, useItemNotes} from '@/hooks/sgsst/evaluacion/use-item-notes'
import {SUGERIDO_KIND_LABEL_KEY, useConfirmarPendientes} from '@/hooks/sgsst/evaluacion/use-sgsst-pendientes'
import {type SgsstItem, type SgsstItemStatus} from '@/hooks/sgsst/evaluacion/use-sgsst'
import {soportesIncompletos} from '@/lib/sgsst-readiness'
import {formatDate, formatRatePct} from '@/lib/risk'
import {translateApiError} from '@/lib/errors'
import {cn} from '@/lib/utils'

const STATUS_TONE: Record<string, 'green' | 'red' | 'neutral'> = {
  cumple: 'green',
  no_cumple: 'red',
  no_aplica: 'neutral',
}

export const MARK_PRESSED: Record<'cumple' | 'no_cumple' | 'no_aplica', string> = {
  cumple: 'bg-accent text-accent-foreground',
  no_cumple: 'tone-red',
  no_aplica: 'bg-card text-foreground',
}

// Mirrors backend/app/routers/ipevr.py::CANDIDATE_41X_CODES exactly; change both together.
export const IPEVR_41X_CODES = new Set(['4.1.1', '4.1.2', '4.1.3', '4.2.1', '4.2.5', '4.2.6'])

/** Sin `inline-flex`: borra el marcador del `<details>`, que es la afordancia. */
const DISCLOSURE = 'cursor-pointer text-[11.5px] font-medium text-muted-foreground hover:text-foreground'

const DISCLOSURE_SLOT = 'min-w-0 open:w-full'

function ItemTrace({item}: {item: SgsstItem}) {
  const {t} = useTranslation()
  if (!item.trace_kind || !item.trace_at) return null
  return (
    <p className="mt-0.5 flex items-start gap-1.5 text-xs text-muted-foreground" data-testid="item-trace">
      <span aria-hidden="true" className="mt-1 inline-block h-[5px] w-[5px] shrink-0 rounded-full bg-primary" />
      <span className="min-w-0 break-words">
        {item.trace_kind === 'resolucion'
          ? t('sgsst.trace.resolved', {date: formatDate(item.trace_at)})
          : t('sgsst.trace.corrected', {date: formatDate(item.trace_at), note: item.trace_note ?? ''})}
        {item.trace_kind === 'correccion' && item.previous_status && (
          <span className="mt-0.5 block" data-testid="correction-provenance">
            {t('marcado.trace.correctedBy', {
              previous: t(`sgsst.status.${item.previous_status}`),
              name: item.changed_by ? `${item.changed_by.name} ${item.changed_by.last_name}` : '',
              date: formatDate(item.trace_at),
            })}
          </span>
        )}
      </span>
    </p>
  )
}

function MarkedByLine({item}: {item: SgsstItem}) {
  const {t} = useTranslation()
  if (!item.status || !item.confirmado_por || !item.status_changed_at) return null
  return (
    <p className="mt-0.5 text-xs text-muted-foreground" data-testid="marcado-por">
      {t('marcado.provenance.markedBy', {
        name: `${item.confirmado_por.name} ${item.confirmado_por.last_name}`,
        date: formatDate(item.status_changed_at),
      })}
    </p>
  )
}

function SugeridoWhyTrace({item}: {item: SgsstItem}) {
  const {t} = useTranslation()
  if (!item.sugerido || !item.sugerido_backed_by_kind) return null
  const artifact = t(SUGERIDO_KIND_LABEL_KEY[item.sugerido_backed_by_kind])
  return (
    <details className="mt-0.5">
      <summary
        className="cursor-pointer text-xs text-muted-foreground hover:text-foreground"
        data-testid="sugerido-why-toggle">
        {t('marcado.sugerido.whyToggle')}
      </summary>
      <p className="mt-1 text-xs text-muted-foreground" data-testid="sugerido-why-trace">
        {item.sugerido_backed_by_version
          ? t('marcado.sugerido.traceWithVersion', {artifact, version: item.sugerido_backed_by_version})
          : t('marcado.sugerido.trace', {artifact})}
      </p>
    </details>
  )
}

// U4: el porqué del atajo va una sola vez, en el encabezado del grupo (`Ipevr41xGroupHelp`).
function Ipevr41xHint({businessId, code}: {businessId: string; code: string}) {
  const {t} = useTranslation()
  if (!IPEVR_41X_CODES.has(code)) return null
  return (
    <p className="mt-0.5 flex items-center gap-1 text-xs" data-testid="ipevr-41x-hint">
      <Link
        to={`/businesses/${businessId}/sgsst/matriz-riesgos`}
        className="font-medium text-primary hover:underline"
        data-testid="ipevr-41x-link">
        {t('sgsst.items.ipevrHint.link')}
      </Link>
    </p>
  )
}

/** T11: el rótulo nombra el enlace a la matriz, no el grupo: solo 6 de sus 11 estándares lo llevan. */
export function Ipevr41xGroupHelp() {
  const {t} = useTranslation()
  return (
    <HelpIcon label={t('sgsst.items.ipevrHint.grupoAyudaLabel')} testId="ipevr-41x-help">
      {t('sgsst.items.ipevrHint.grupoAyuda')}
    </HelpIcon>
  )
}

// Fetches only once opened, so a 60-item checklist never fires 60 requests up front.
function ItemNotesPanel({businessId, code, canEdit}: {businessId: string; code: string; canEdit: boolean}) {
  const {t} = useTranslation()
  const [open, setOpen] = useState(false)
  // Al agregar se vuelve a la página 1: la nota más reciente va primero.
  const [page, setPage] = useState(1)
  const {data: notesPage} = useItemNotes(businessId, code, open, page)
  const notes = notesPage?.items
  const addNote = useAddItemNote(businessId, code)
  const deleteNote = useDeleteItemNote(businessId, code)
  return (
    <details
      className={DISCLOSURE_SLOT}
      onToggle={(e) => setOpen(e.currentTarget.open)}
      data-testid={`item-notes-${code}`}>
      <summary className={DISCLOSURE}>
        {notesPage ? t('sgsst.itemNotes.summaryCount', {count: notesPage.total}) : t('sgsst.itemNotes.summary')}
      </summary>
      <div className="mt-2 rounded-lg bg-secondary/50 p-3">
        <ActionNotesThread
          notes={notes ?? []}
          editable={canEdit}
          adding={addNote.isPending}
          deleteNote={deleteNote}
          onAdd={async (text) => {
            await addNote.mutateAsync(text)
            setPage(1)
          }}
        />
        {notesPage && notesPage.total > 0 && (
          <PaginationBar page={page} pageSize={NOTAS_PAGE_SIZE} total={notesPage.total} onPageChange={setPage} />
        )}
      </div>
    </details>
  )
}

export function ItemRow({
  item,
  fase2,
  canEdit,
  canAttest,
  firmaBlockedReason,
  canConfirmSugerido,
  disenoBlockedReason,
  onMark,
  onUndo,
  onCorrect,
  onAddAction,
  actionCount,
  planHref,
  businessId,
  evidence,
  solicitudes,
  roster,
  autoReveal,
  needsDesignation,
  sinSst,
  sinSoporte,
  coverage,
}: {
  item: SgsstItem
  fase2: boolean
  canEdit: boolean
  // Puerta de FIRMA (art. 27, por rol): marcar / deshacer / corregir.
  canAttest: boolean
  firmaBlockedReason: string | null
  // Puerta de DISEÑO (licencia para la banda, arts. 4/10/17): solo confirmar un sugerido la lee.
  canConfirmSugerido: boolean
  disenoBlockedReason: string | null
  onMark: (item: SgsstItem, status: SgsstItemStatus) => void
  onUndo: (item: SgsstItem) => void
  onCorrect: (item: SgsstItem) => void
  onAddAction: (item: SgsstItem, titulo?: string) => void
  actionCount: number
  planHref: string
  businessId: string
  evidence: Evidence[]
  solicitudes: Solicitud[]
  roster: Employee[]
  autoReveal: boolean
  needsDesignation: boolean
  /** U3: el aviso sin SST de la fila 1.1.1; la página decide cuándo montarlo. */
  sinSst?: ReactNode
  sinSoporte: boolean
  coverage: {year: number; isClosed: boolean; workerCount: number}
}) {
  const {t} = useTranslation()
  const {can_upload_evidence: canUploadEvidence} = useCapabilities(businessId)
  const pendingCount = evidence.filter((e) => e.pending_confirmation).length
  const liRef = useRef<HTMLLIElement>(null)
  const evidenceRef = useRef<HTMLDetailsElement>(null)
  // The «Registrar designación» nudge hides while the panel (with its own button) is open.
  const [evidencePanelOpen, setEvidencePanelOpen] = useState(false)
  const confirmSugerido = useConfirmarPendientes(businessId)
  const [confirmError, setConfirmError] = useState<string | null>(null)
  // `location.key` in deps (KAI-148): navigating again to the same `#std-x` must reveal the row again.
  const {key: locationKey} = useLocation()
  useEffect(() => {
    if (autoReveal && liRef.current && evidenceRef.current) {
      evidenceRef.current.open = true
      setEvidencePanelOpen(true)
      liRef.current.scrollIntoView({block: 'center'})
    }
  }, [autoReveal, locationKey])
  function revealEvidencePanel() {
    if (evidenceRef.current) {
      evidenceRef.current.open = true
      setEvidencePanelOpen(true)
      evidenceRef.current.scrollIntoView({block: 'center'})
    }
  }
  return (
    // `-mx-4 px-4` contra el `px-4` del `<ul>`: el hover y la regla recorren la tarjeta entera.
    <li
      ref={liRef}
      id={`std-${item.code}`}
      className="-mx-4 border-b border-border px-4 py-3 transition-colors last:border-b-0 hover:bg-secondary/30">
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-start">
        <span className="shrink-0 pt-0.5 font-mono text-xs font-medium text-muted-foreground sm:w-12">{item.code}</span>
        {/* `sm:basis-72`: con base 0, una fila cargada de acciones aplastaba el nombre a una palabra
            por línea en vez de envolver las acciones. */}
        <div className="min-w-0 flex-1 sm:basis-72">
          <p className="text-sm text-foreground">
            {item.name} <span className="font-mono text-xs text-muted-foreground">· {formatRatePct(item.weight)}</span>
          </p>
          <ItemTrace item={item} />
          <MarkedByLine item={item} />
          <SugeridoWhyTrace item={item} />
          <Ipevr41xHint businessId={businessId} code={item.code} />
          {item.code === '4.1.2' && (
            <BateriaSolicitudBlock businessId={businessId} standardStatus={item.status} canEdit={canEdit} />
          )}
          {item.code === '3.1.1' && <PerfilSociodemograficoCard businessId={businessId} />}
          {sinSst && <div className="mt-1.5">{sinSst}</div>}
          {fase2 && canEdit && (
            <AccionSugeridaList
              acciones={item.acciones_sugeridas}
              onCrear={(titulo) => onAddAction(item, titulo)}
              ayuda
            />
          )}
          {confirmError && (
            <p className="mt-0.5 text-xs text-destructive" data-testid="confirm-sugerido-error">
              {confirmError}
            </p>
          )}
          {item.status === 'no_aplica' && item.justification && (
            <p className="mt-0.5 text-xs text-muted-foreground italic">
              {t('sgsst.items.justifiedAs', {justification: item.justification})}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
          {needsDesignation && (
            <span className="flex flex-wrap items-center gap-2" data-testid={`designation-cta-${item.code}`}>
              <Tag tone="amber">{t('sgsst.designationCta.badge')}</Tag>
              {/* Una salida por fila: con `sinSst` la puerta ya es `SinSstAccion`; el chip se queda. */}
              {!evidencePanelOpen && !sinSst && (
                <Button
                  size="sm"
                  variant="outline"
                  data-testid={`designation-cta-action-${item.code}`}
                  onClick={revealEvidencePanel}>
                  {t('sgsst.designationCta.action')}
                </Button>
              )}
            </span>
          )}
          <ConstructorBlock businessId={businessId} code={item.code} canEdit={canEdit} />
          {sinSoporte && <Tag tone="amber">{t('sgsst.readiness.rowBadge')}</Tag>}
          {/* Advisory only, never blocks marking (soportes esperados spec §6, D4). */}
          {!sinSoporte && soportesIncompletos(item) && (
            <Tag tone="amber" data-testid={`soportes-incompletos-${item.code}`}>
              {t('sgsst.readiness.soportesRowBadge')}
            </Tag>
          )}
          {item.sugerido && (
            <Tag tone="enCurso" data-testid="sugerido-chip">
              {t('marcado.sugeridoChip')}
            </Tag>
          )}
          {item.sugerido && canEdit && item.sugerencia_id && (
            <Button
              size="sm"
              variant="outline"
              disabled={!canConfirmSugerido || confirmSugerido.isPending}
              title={gateTooltip(canConfirmSugerido, disenoBlockedReason, t)}
              onClick={() => {
                setConfirmError(null)
                confirmSugerido.mutate([item.sugerencia_id!], {
                  onError: (err) => setConfirmError(translateApiError(err)),
                })
              }}
              data-testid="confirm-sugerido">
              {t('marcado.pendientes.confirmOne')}
            </Button>
          )}
          {!item.sugerido && item.carried_previous_cumple && (
            <Tag tone="amber" data-testid="carried-forward-chip">
              {t('marcado.carriedForward')}
            </Tag>
          )}
          {item.status ? (
            <Tag tone={STATUS_TONE[item.status]} data-testid="item-status">
              {t(`sgsst.status.${item.status}`)}
            </Tag>
          ) : (
            <span className="font-mono text-[10px] tracking-wider text-muted-foreground uppercase">
              {t('sgsst.status.pending')}
            </span>
          )}
          {!fase2 && canEdit && (
            <div className="flex gap-0.5 rounded-lg bg-secondary p-0.5" role="group">
              {(['cumple', 'no_cumple', 'no_aplica'] as const).map((status) => (
                <button
                  key={status}
                  type="button"
                  aria-pressed={item.status === status}
                  disabled={!canAttest}
                  title={gateTooltip(canAttest, firmaBlockedReason, t)}
                  onClick={() => onMark(item, status)}
                  className={cn(
                    'rounded-md px-2 py-1 text-xs whitespace-nowrap transition-colors disabled:cursor-not-allowed disabled:opacity-40 disabled:saturate-50',
                    item.status === status
                      ? cn('font-semibold shadow-xs', MARK_PRESSED[status])
                      : 'text-muted-foreground hover:bg-card hover:text-foreground'
                  )}>
                  {t(`sgsst.controls.${status}`)}
                </button>
              ))}
            </div>
          )}
          {!fase2 && canEdit && item.status && (
            <button
              type="button"
              data-testid="undo-item"
              disabled={!canAttest}
              title={gateTooltip(canAttest, firmaBlockedReason, t)}
              onClick={() => onUndo(item)}
              className="text-xs whitespace-nowrap text-muted-foreground underline decoration-dotted underline-offset-3 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline">
              {t('sgsst.controls.undo')}
            </button>
          )}
          {fase2 && item.status === 'no_cumple' && actionCount > 0 && (
            <Link
              to={planHref}
              data-testid="plan-count-link"
              className="text-xs font-medium whitespace-nowrap text-primary hover:underline">
              {t('sgsst.planCount', {count: actionCount})}
            </Link>
          )}
          {fase2 && canEdit && item.status === 'no_cumple' && actionCount === 0 && (
            <Button size="sm" variant="outline" data-testid="add-action" onClick={() => onAddAction(item)}>
              {t('sgsst.addAction')}
            </Button>
          )}
          {fase2 && canEdit && (
            <button
              type="button"
              data-testid="correct-item"
              disabled={!canAttest}
              title={gateTooltip(canAttest, firmaBlockedReason, t)}
              onClick={() => onCorrect(item)}
              className="text-xs whitespace-nowrap text-muted-foreground underline decoration-dotted underline-offset-3 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline">
              {t('sgsst.correction.action')}
            </button>
          )}
        </div>
      </div>
      <div className="mt-2.5 flex flex-wrap items-start gap-x-5 gap-y-1.5 border-t border-dashed border-border/70 pt-2">
        <details className={DISCLOSURE_SLOT}>
          <summary className={DISCLOSURE}>{t('sgsst.items.showCriteria')}</summary>
          <div className="mt-2 space-y-2 rounded-lg bg-secondary/50 p-3 text-xs text-muted-foreground">
            <p>
              <span className="font-medium text-foreground">{t('sgsst.items.criterio')}: </span>
              {item.criterio}
            </p>
            <p>
              <span className="font-medium text-foreground">{t('sgsst.items.modo')}: </span>
              {item.modo_verificacion}
            </p>
          </div>
        </details>
        <details
          ref={evidenceRef}
          className={DISCLOSURE_SLOT}
          onToggle={(e) => setEvidencePanelOpen(e.currentTarget.open)}>
          <summary className={DISCLOSURE}>
            {t('evidence.summary', {count: evidence.length})}
            {pendingCount > 0 && (
              <span className="tone-amber ml-2 rounded-full px-2 py-0.5 font-mono text-[10px]">
                {t('evidence.pendingCount', {count: pendingCount})}
              </span>
            )}
          </summary>
          <EvidencePanel
            businessId={businessId}
            standardCode={item.code}
            standardName={item.name}
            items={evidence}
            solicitudes={solicitudes}
            roster={roster}
            // Free tier: only evidence is paid; the checklist and the autoevaluación stay free.
            editable={canEdit && canUploadEvidence}
            coverage={{item, ...coverage}}
          />
        </details>
        <ItemNotesPanel businessId={businessId} code={item.code} canEdit={canEdit} />
      </div>
    </li>
  )
}
